// src/pages/LoginPage.tsx
//
// Kolmivaiheinen login-flow (5.6.2026):
//   1. 'credentials' — email + password
//   2a. 'twoFactor' — kun backend palauttaa requires2fa → 6-numeroisen koodin syöttö
//   2b. 'enrollmentQr' → 'enrollmentConfirm' → 'enrollmentBackup' —
//        kun backend palauttaa requiresEnrollment (C7-pakotus manager/admin/ceo-rooleille)
//
// Backup-koodit näytetään KERRAN — käyttäjän vastuu tallentaa.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../api/auth';
import { twoFactorApi } from '../api/client';
import type { EnrollInitResponse } from '../api/types';

const LOGIN_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&display=swap');

  .login-root {
    min-height: 100vh;
    min-height: 100dvh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: max(16px, env(safe-area-inset-top)) 16px max(16px, env(safe-area-inset-bottom));
    box-sizing: border-box;
    background:
      radial-gradient(1100px 520px at 15% -10%, rgba(60,124,156,0.16), transparent 60%),
      radial-gradient(900px 480px at 110% 110%, rgba(42,106,138,0.14), transparent 55%),
      linear-gradient(160deg, #eef3f7 0%, #f6f9fb 45%, #e8eff5 100%);
    font-family: var(--font-ui);
    touch-action: manipulation;
  }
  /* 17.7.2026 (Aleksanterin palaute): responsiivinen kortti — ei koskaan yli
     reunojen mobiilissa; padding skaalautuu pienillä näytöillä. */
  .login-box {
    background: rgba(255,255,255,0.92);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    border: 1px solid rgba(208,216,224,0.8);
    border-radius: 16px;
    padding: clamp(26px, 6vw, 44px) clamp(20px, 6vw, 40px) clamp(24px, 5vw, 36px);
    width: min(400px, 100%);
    box-shadow: 0 20px 50px rgba(26,42,54,0.10), 0 2px 8px rgba(26,42,54,0.05);
    position: relative;
    overflow: hidden;
    box-sizing: border-box;
  }
  .login-box.wide { width: min(500px, 100%); }
  .login-box::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #2a6a8a, #3c7c9c, #5a9cbf);
  }
  .login-logo {
    font-size: clamp(24px, 6vw, 28px);
    font-weight: 700;
    color: #0f1923;
    margin-bottom: 6px;
    letter-spacing: -0.02em;
    text-align: center;
  }
  .login-logo span {
    background: linear-gradient(90deg, #2a6a8a, #5a9cbf);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    font-weight: 400;
  }
  .login-welcome {
    font-size: 13px;
    color: #7a8f9e;
    margin-bottom: 28px;
    text-align: center;
  }
  .login-form-group { margin-bottom: 18px; }
  .login-label {
    display: block;
    font-size: 12px;
    font-weight: 500;
    color: #5a6b78;
    margin-bottom: 6px;
  }
  .login-input {
    width: 100%;
    background: #f8fafb;
    border: 1px solid #d0d8e0;
    border-radius: 5px;
    padding: 10px 14px;
    font-size: 16px;
    color: #1a2a36;
    outline: none;
    box-sizing: border-box;
    transition: border-color 150ms, box-shadow 150ms;
    font-family: inherit;
  }
  .login-input:focus {
    border-color: #487ea0;
    box-shadow: 0 0 0 3px rgba(72,126,160,0.1);
    background: #fff;
  }
  .login-input::placeholder { color: #b0bec5; }
  .login-input.code {
    text-align: center;
    letter-spacing: 0.4em;
    font-size: 22px;
    font-family: 'Menlo', monospace;
  }
  .login-btn {
    font-family: inherit;
    font-size: 14px;
    font-weight: 500;
    letter-spacing: 0.02em;
    padding: 11px 20px;
    border: none;
    border-radius: 5px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: background .15s, transform .1s;
    width: 100%;
    background: linear-gradient(135deg, #2a6a8a, #3c7c9c);
    color: #fff;
    margin-top: 4px;
    font-weight: 600;
    box-shadow: 0 2px 10px rgba(42,106,138,0.28);
  }
  .login-btn:hover:not(:disabled) { background: linear-gradient(135deg, #235a76, #336a86); }
  .login-btn:active:not(:disabled) { transform: scale(0.99); }
  .login-btn:disabled { opacity: .5; cursor: not-allowed; }
  .login-btn.secondary { background: transparent; color: #487ea0; border: 1px solid #d0d8e0; margin-top: 8px; }
  .login-btn.secondary:hover:not(:disabled) { background: #f8fafb; }
  .login-error {
    padding: 10px 14px;
    font-size: 13px;
    border-left: 3px solid #c0392b;
    border-radius: 0 5px 5px 0;
    background: #fdf0ee;
    color: #c0392b;
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;
  }
  .login-info {
    padding: 10px 14px;
    font-size: 13px;
    border-left: 3px solid #3a6d8c;
    border-radius: 0 5px 5px 0;
    background: #eef4f8;
    color: #1e3a5f;
    margin-bottom: 16px;
    line-height: 1.5;
  }
  .login-footer {
    text-align: center;
    margin-top: 24px;
    font-size: 11px;
    color: #a0b0bc;
  }
  .login-spinner {
    display: inline-block; width: 12px; height: 12px;
    border: 2px solid currentColor; border-top-color: transparent;
    border-radius: 50%; animation: login-spin .7s linear infinite;
  }
  @keyframes login-spin { to { transform: rotate(360deg); } }

  .login-qr {
    display: block;
    margin: 12px auto 16px;
    width: 240px;
    height: 240px;
    border: 1px solid #d0d8e0;
    border-radius: 8px;
    background: #fff;
  }
  .login-secret {
    text-align: center;
    font-family: 'Menlo', monospace;
    font-size: 13px;
    color: #5a6b78;
    background: #f8fafb;
    padding: 8px 14px;
    border-radius: 4px;
    margin-bottom: 16px;
    word-break: break-all;
  }
  .login-backup-list {
    list-style: none;
    padding: 16px;
    margin: 0 0 16px;
    background: #fffbeb;
    border: 1px solid #fde68a;
    border-radius: 6px;
    font-family: 'Menlo', monospace;
    font-size: 14px;
    color: #1a2a36;
  }
  .login-backup-list li {
    padding: 4px 8px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .login-backup-list li:nth-child(odd) { background: rgba(0,0,0,0.02); }
  .login-warn {
    font-size: 12px;
    color: #92400e;
    background: #fef3c7;
    padding: 10px 14px;
    border-radius: 5px;
    margin-bottom: 16px;
    line-height: 1.4;
  }
`;

type Stage =
  | 'credentials'
  | 'twoFactor'
  | 'enrollmentQr'
  | 'enrollmentConfirm'
  | 'enrollmentBackup';

export function LoginPage() {
  const auth = useAuth();
  const navigate = useNavigate();

  // Vaihe 1 — credentials
  const [stage, setStage] = useState<Stage>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Vaihe 2a — 2FA-koodi
  const [twoFaToken, setTwoFaToken] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);

  // Vaihe 2b — enrollment
  const [enrollmentToken, setEnrollmentToken] = useState<string | null>(null);
  const [qrData, setQrData] = useState<EnrollInitResponse | null>(null);
  const [enrollCode, setEnrollCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleCredentialsSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const result = await auth.login({ email, password });
      if (result.status === 'ok') {
        navigate('/', { replace: true });
      } else if (result.status === 'requires2fa') {
        setTwoFaToken(result.twoFaToken);
        setStage('twoFactor');
      } else if (result.status === 'requiresEnrollment') {
        // C7: hae heti QR-koodi ja siirry enrollment-vaiheeseen
        setEnrollmentToken(result.enrollmentToken);
        const qr = await twoFactorApi.initialEnrollInit(result.enrollmentToken);
        setQrData(qr);
        setStage('enrollmentQr');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kirjautuminen epäonnistui');
    } finally {
      setLoading(false);
    }
  }

  async function handleTwoFactorSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!twoFaToken) return;
    setError(null);
    setLoading(true);
    try {
      await auth.completeTwoFactor(twoFaToken, code, trustDevice);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : '2FA-koodin tarkistus epäonnistui');
    } finally {
      setLoading(false);
    }
  }

  async function handleEnrollmentConfirmSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!enrollmentToken) return;
    setError(null);
    setLoading(true);
    try {
      const { backupCodes } = await auth.completeInitialEnrollment(enrollmentToken, enrollCode);
      setBackupCodes(backupCodes);
      setStage('enrollmentBackup');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Vahvistus epäonnistui');
    } finally {
      setLoading(false);
    }
  }

  function backToCredentials() {
    setStage('credentials');
    setError(null);
    setCode('');
    setEnrollCode('');
    setTwoFaToken(null);
    setEnrollmentToken(null);
    setQrData(null);
  }

  function handleBackupAcknowledged() {
    // Pyydä käyttäjää kirjautumaan uudelleen — nyt 2FA on enabled
    backToCredentials();
  }

  return (
    <>
      <style>{LOGIN_CSS}</style>
      <div className="login-root">
        <div className={`login-box ${stage !== 'credentials' && stage !== 'twoFactor' ? 'wide' : ''}`}>
          <div className="login-logo">Savroc <span>ERP</span></div>

          {/* Stage: credentials */}
          {stage === 'credentials' && (
            <>
              <div className="login-welcome">Kirjaudu sisään jatkaaksesi</div>
              {/* Multi-tab-identiteettivahti: kerro MIKSI kirjattiin ulos */}
              {auth.authNotice && !error && (
                <div className="login-error" style={{ background: 'rgba(217,119,6,0.12)', color: '#92400e' }}>
                  <span>⚠</span> {auth.authNotice}
                </div>
              )}
              <form onSubmit={handleCredentialsSubmit}>
                <div className="login-form-group">
                  <label className="login-label" htmlFor="email">Sähköposti</label>
                  <input
                    id="email"
                    type="email"
                    className="login-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    autoFocus
                    required
                  />
                </div>

                <div className="login-form-group">
                  <label className="login-label" htmlFor="password">Salasana</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      className="login-input"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                      required
                      style={{ paddingRight: 44 }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(v => !v)}
                      aria-label={showPassword ? 'Piilota salasana' : 'Näytä salasana'}
                      title={showPassword ? 'Piilota salasana' : 'Näytä salasana'}
                      style={{
                        position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                        background: 'transparent', border: 'none', cursor: 'pointer',
                        fontSize: 18, color: '#64748b', padding: '4px 8px',
                      }}
                    >
                      {showPassword ? '🙈' : '👁'}
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="login-error">
                    <span>✕</span> {error}
                  </div>
                )}

                <button type="submit" className="login-btn" disabled={loading}>
                  {loading ? <><span className="login-spinner" /> Kirjaudutaan...</> : 'Kirjaudu sisään'}
                </button>
              </form>
            </>
          )}

          {/* Stage: 2FA-koodi */}
          {stage === 'twoFactor' && (
            <>
              <div className="login-welcome">🔐 Vahvista kirjautuminen</div>
              <div className="login-info">
                Syötä 6-numeroinen koodi Authenticator-sovelluksestasi.
                Voit myös käyttää backup-koodia muodossa <code>ABCD-1234</code> jos
                puhelin ei ole käytettävissä.
              </div>
              <form onSubmit={handleTwoFactorSubmit}>
                <div className="login-form-group">
                  <label className="login-label" htmlFor="code">2FA-koodi</label>
                  <input
                    id="code"
                    type="text"
                    className="login-input code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    inputMode="text"
                    autoComplete="one-time-code"
                    autoFocus
                    required
                    maxLength={20}
                  />
                </div>

                <div className="login-form-group" style={{ marginTop: '12px' }}>
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      color: '#5a6b78',
                      fontSize: '0.9rem',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={trustDevice}
                      onChange={(e) => setTrustDevice(e.target.checked)}
                      style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#487ea0' }}
                    />
                    Muista tämä laite 30 päivää (ei 2FA-pyyntöä)
                  </label>
                </div>

                {error && (
                  <div className="login-error">
                    <span>✕</span> {error}
                  </div>
                )}

                <button type="submit" className="login-btn" disabled={loading || code.length < 6}>
                  {loading ? <><span className="login-spinner" /> Tarkistetaan...</> : 'Vahvista'}
                </button>
                <button type="button" className="login-btn secondary" onClick={backToCredentials}>
                  ← Takaisin
                </button>
              </form>
            </>
          )}

          {/* Stage: enrollment vaihe 1 — QR */}
          {stage === 'enrollmentQr' && qrData && (
            <>
              <div className="login-welcome">🔐 Aktivoi 2FA jatkaaksesi</div>
              <div className="login-info">
                Roolisi vaatii kaksivaiheisen tunnistautumisen (2FA).
                <br /><br />
                <strong>1.</strong> Asenna puhelimeen Authenticator-sovellus:
                Google Authenticator, Microsoft Authenticator tai Authy.
                <br />
                <strong>2.</strong> Skannaa alla oleva QR-koodi sovelluksella.
              </div>

              <img src={qrData.qrCodeDataUrl} alt="2FA QR-koodi" className="login-qr" />

              <div className="login-label" style={{ textAlign: 'center', marginBottom: 4 }}>
                Manuaalinen syöttö (jos QR ei toimi):
              </div>
              <div className="login-secret">{qrData.secret}</div>

              <button
                type="button"
                className="login-btn"
                onClick={() => setStage('enrollmentConfirm')}
              >
                Skannattu — jatka →
              </button>
              <button type="button" className="login-btn secondary" onClick={backToCredentials}>
                ← Peruuta
              </button>
            </>
          )}

          {/* Stage: enrollment vaihe 2 — koodin vahvistus */}
          {stage === 'enrollmentConfirm' && (
            <>
              <div className="login-welcome">🔐 Vahvista enrollment</div>
              <div className="login-info">
                Syötä Authenticator-sovelluksen näyttämä 6-numeroinen koodi
                aktivoidaksesi 2FA:n.
              </div>
              <form onSubmit={handleEnrollmentConfirmSubmit}>
                <div className="login-form-group">
                  <label className="login-label" htmlFor="enrollCode">Vahvistuskoodi</label>
                  <input
                    id="enrollCode"
                    type="text"
                    className="login-input code"
                    value={enrollCode}
                    onChange={(e) => setEnrollCode(e.target.value)}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoFocus
                    required
                    maxLength={6}
                    pattern="\d{6}"
                  />
                </div>

                {error && (
                  <div className="login-error">
                    <span>✕</span> {error}
                  </div>
                )}

                <button type="submit" className="login-btn" disabled={loading || enrollCode.length !== 6}>
                  {loading ? <><span className="login-spinner" /> Tarkistetaan...</> : 'Aktivoi 2FA'}
                </button>
                <button type="button" className="login-btn secondary" onClick={() => setStage('enrollmentQr')}>
                  ← Näytä QR uudelleen
                </button>
              </form>
            </>
          )}

          {/* Stage: enrollment vaihe 3 — backup-koodit */}
          {stage === 'enrollmentBackup' && (
            <>
              <div className="login-welcome">🎉 2FA aktivoitu</div>
              <div className="login-warn">
                ⚠️ <strong>TALLENNA NÄMÄ BACKUP-KOODIT NYT.</strong> Et näe niitä uudelleen.
                Käytä jos puhelin häviää — jokainen koodi kelpaa kerran.
              </div>

              <ol className="login-backup-list" style={{ counterReset: 'item', paddingLeft: 20 }}>
                {backupCodes.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ol>

              <button
                type="button"
                className="login-btn"
                onClick={() => {
                  // Kopioi leikepöydälle
                  const text = backupCodes.join('\n');
                  navigator.clipboard?.writeText(text).then(
                    () => alert('Backup-koodit kopioitu leikepöydälle'),
                    () => alert('Kopiointi epäonnistui — kirjoita koodit käsin ylös'),
                  );
                }}
              >
                📋 Kopioi leikepöydälle
              </button>

              <button
                type="button"
                className="login-btn secondary"
                onClick={handleBackupAcknowledged}
              >
                Olen tallentanut koodit — kirjaudu uudelleen
              </button>
            </>
          )}

          <div className="login-footer">Savroc Oy</div>
        </div>
      </div>
    </>
  );
}
